import React, { useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import ListItem from '@mui/material/ListItem';
import Checkbox from '@mui/material/Checkbox';
import CheckIcon from '@mui/icons-material/Check';
import CircleIcon from '@mui/icons-material/Circle';
import HistogramPlot from '../Plots/HistogramPlot';
import { SensorFilteredStats } from './helpers';
import { useAppDispatch, useAppSelector } from '../../state/store';
import { getActiveMarker } from '../../state/selectors';
import { setSelectedSensors } from '../../state/actions';
import { combineStats, Stats } from '../../utils/statistics';
import { themeProps } from '../../styles/theme';
import InlineBarPlot from '../Plots/InlineBarPlot';
import ValuePill from './ValuePill';
import { SpaceParams } from '../Map/mapHelpers';

const TagListItem = React.memo(
  ({
    space,
    allSpaces,
    summaryValue,
    color,
    valuesRange,
    selectedSpaces,
    setSelectedSpaces,
    sensorFilteredStats,
    showHistogram,
  }: {
    space: SpaceParams;
    allSpaces: SpaceParams[];
    summaryValue: number | undefined;
    color: string;
    valuesRange: [number, number];
    selectedSpaces: string[];
    setSelectedSpaces: (value: string[]) => void;
    sensorFilteredStats: Map<string, SensorFilteredStats>;
    showHistogram: boolean;
  }) => {
    const theme = useTheme();
    const activeMarker = useAppSelector(getActiveMarker);
    const dispatch = useAppDispatch();
    const [isHovered, setIsHovered] = useState(false);

    const handleMouseEnter = (spaceItem: SpaceParams) => {
      setIsHovered(true);
      dispatch(setSelectedSensors(spaceItem.sensors));
    };

    const handleMouseLeave = () => {
      setIsHovered(false);
      if (selectedSpaces) {
        const spaceSensors: string[] = [];

        selectedSpaces.forEach((spaceId: string) => {
          const spaceDetail = allSpaces.find((item) => item.title === spaceId);
          if (spaceDetail) spaceSensors.push(...spaceDetail.sensors);
        });
        dispatch(setSelectedSensors(spaceSensors));
      } else dispatch(setSelectedSensors([]));
    };

    const toggleCheckBox = (spaceName: string) => {
      const newSpaces = [...selectedSpaces];
      const spacePos = newSpaces.indexOf(spaceName);

      if (spacePos === -1) newSpaces.push(spaceName);
      else delete newSpaces[spacePos];
      setSelectedSpaces(newSpaces);
    };

    function getSpaceInlineValues(spaceItem: SpaceParams): number[] {
      const allInlineValues: (number | null | undefined)[] = [];

      spaceItem.sensors.forEach((sensorId: string) => {
        const sensorData = sensorFilteredStats.get(sensorId);
        const stats = sensorData?.stats;

        let inlineValues: number[] | undefined;

        if (stats?.totalAdded !== undefined) {
          inlineValues = stats.values?.totVals;
        } else if (stats?.utl !== undefined) {
          inlineValues = stats.values?.utlVals?.map((v: number) => v * 100);
        } else {
          inlineValues = stats?.values?.avgVals;
        }

        if (inlineValues) {
          allInlineValues.push(...inlineValues);
        }
      });

      // Filter out any null/undefined values and return clean number array
      return allInlineValues.filter(
        // eslint-disable-next-line no-restricted-globals
        (value): value is number => typeof value === 'number' && !isNaN(value)
      );
    }

    const combinedStats = useMemo(() => {
      if (showHistogram) return null;
      const allStats: Stats[] = [];
      space.sensors.forEach((sensor) => {
        const sensorStats = sensorFilteredStats.get(sensor)?.stats;
        if (sensorStats) allStats.push(sensorStats);
      });
      return combineStats(allStats);
    }, [sensorFilteredStats, showHistogram, space.sensors]);

    return (
      <ListItem
        onMouseEnter={() => handleMouseEnter(space)}
        onMouseLeave={handleMouseLeave}
        sx={{ backgroundColor: isHovered ? `${color}66` : 'transparent' }}
      >
        <Grid container spacing={1}>
          <Grid size={{ md: 1, sm: 1, xs: 2 }} sx={{ marginLeft: '5px', alignSelf: 'center' }}>
            <Checkbox
              icon={
                <CircleIcon
                  style={{
                    color: 'transparent',
                    borderRadius: '50%',
                    border: `solid 4px ${color ?? themeProps.colors.lightGrey}`,
                  }}
                />
              }
              checkedIcon={
                <CheckIcon
                  style={{
                    color: theme.palette.text.primary,
                    borderRadius: '50%',
                    border: `dashed 4px ${color ?? themeProps.colors.lightGrey}`,
                  }}
                />
              }
              checked={selectedSpaces !== undefined && selectedSpaces.indexOf(space.title) !== -1}
              onChange={() => toggleCheckBox(space.title)}
            />{' '}
          </Grid>
          <Grid size={{ md: 2, sm: 2, xs: 2 }} sx={{ alignSelf: 'center' }}>
            <Typography variant="body1">{space.title}</Typography>
          </Grid>
          <Grid
            size={{ md: 1, sm: 1, xs: 5 }}
            sx={{ textAlign: { xs: 'end', sm: 'center' }, alignSelf: 'center' }}
          >
            <ValuePill value={summaryValue} />
          </Grid>
          <Grid size={{ md: 6, sm: 6, xs: 10 }} sx={{ textAlign: 'center', alignSelf: 'center' }}>
            {showHistogram ? (
              <HistogramPlot
                values={getSpaceInlineValues(space) ?? []}
                sensorName={space.title}
                varName={activeMarker}
                xRange={valuesRange}
              />
            ) : (
              combinedStats &&
              !combinedStats.bandTimes?.has('Unknown') && (
                <InlineBarPlot
                  data={combinedStats}
                  sensorName={space.title ?? 'UNKNOWN'}
                  varName={activeMarker}
                />
              )
            )}
          </Grid>
        </Grid>
      </ListItem>
    );
  }
);

export default TagListItem;
